
import React, { useState, forwardRef } from "react";
import { cn } from "@/lib/utils";
import PasswordToggle from "./password-toggle";
import InputFeedback from "./input-feedback";

export interface InputFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      className,
      type = "text",
      label,
      hint,
      error,
      icon,
      showPasswordToggle = false,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputType = showPasswordToggle
      ? showPassword
        ? "text"
        : "password"
      : type;

    const togglePassword = () => {
      setShowPassword(!showPassword);
    };

    return (
      <div className="space-y-1.5 w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="text-sm font-medium text-foreground/80 mb-1.5 inline-block"
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            "relative flex h-10 w-full rounded-md border border-input bg-background px-3 py-2",
            "transition-colors duration-200 ease-in-out",
            "focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20",
            error && "border-destructive focus-within:border-destructive focus-within:ring-destructive/20",
            className
          )}
        >
          {icon && <div className="flex items-center mr-2 text-muted-foreground">{icon}</div>}
          <input
            type={inputType}
            className={cn(
              "flex-1 w-full bg-transparent text-base placeholder:text-muted-foreground outline-none",
              "placeholder:text-sm focus-none",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            ref={ref}
            {...props}
          />
          {showPasswordToggle && (
            <PasswordToggle 
              showPassword={showPassword} 
              togglePassword={togglePassword} 
            />
          )}
        </div>
        
        <InputFeedback hint={hint} error={error} />
      </div>
    );
  }
);

InputField.displayName = "InputField";

export default InputField;
